import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../services/hrmsService";
import ApprovalDrawer from "./ApprovalDrawer";
import EmployeeSelect from "./EmployeeSelect";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import { EmptyState, Field, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs } from "./hrComponents";
import { cardStyle, formatDate, formatDateTime, iconBtnStyle, inputStyle, pageStyle, requestLabel, statusBadge, statusLabel, tdStyle, thStyle, todayYmd, useLoad } from "./hrUi";

// APR-*: the caller's approval tasks, what they decided, what they asked for, and who
// takes over while they are away.
const TAB = { TODO: "todo", DONE: "done", MINE: "mine", DELEGATE: "delegate" };

function RequestTable({ rows, selectable, selected, onToggle, onOpen, columns }) {
  return (
    <div style={{ ...cardStyle, overflowX: "auto" }}>
      <table className="table mb-0" style={{ minWidth: 640 }}>
        <thead>
          <tr>
            {selectable ? <th style={thStyle} /> : null}
            {columns.map((c) => (
              <th key={c.label} style={thStyle}>
                {c.label}
              </th>
            ))}
            <th style={thStyle} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = r.approver_row_id || r.id;
            return (
              <tr key={key}>
                {selectable ? (
                  <td style={tdStyle}>
                    <input type="checkbox" aria-label="Select request" checked={selected.includes(r.request_id)} onChange={() => onToggle(r.request_id)} />
                  </td>
                ) : null}
                {columns.map((c) => (
                  <td key={c.label} style={tdStyle}>
                    {c.render(r)}
                  </td>
                ))}
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <button type="button" style={{ ...iconBtnStyle, fontSize: 13, fontWeight: 600 }} onClick={() => onOpen(r.request_id || r.id)}>
                    View
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const who = (r) => (
  <>
    <div style={{ fontWeight: 600 }}>{r.requester_name || "—"}</div>
    <div style={{ fontSize: 12, color: "#6B7280" }}>{r.employee_code}</div>
  </>
);

function Inbox({ history, onOpen, refreshKey, onChanged }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getInbox(history), [history, refreshKey]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const rows = data || [];

  useEffect(() => setSelected([]), [data]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const approveSelected = async () => {
    setBusy(true);
    try {
      const res = await hrmsService.bulkApprovalAction(selected, "APPROVE");
      if (res?.success) {
        const failed = (res.data || []).filter((r) => !r.success);
        failed.length ? toast.warn(`${res.message}. ${failed[0].message}`) : toast.success(res.message);
        onChanged();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setBusy(false);
    }
  };

  const columns = history
    ? [
        { label: "Employee", render: who },
        { label: "Request", render: (r) => r.summary || requestLabel(r.module) },
        { label: "My decision", render: (r) => <span style={statusBadge(r.my_status)}>{statusLabel(r.my_status)}</span> },
        { label: "Decided", render: (r) => formatDateTime(r.acted_at) },
      ]
    : [
        { label: "Employee", render: who },
        { label: "Request", render: (r) => r.summary || requestLabel(r.module) },
        { label: "Type", render: (r) => requestLabel(r.module) },
        { label: "Submitted", render: (r) => formatDateTime(r.submitted_at) },
        { label: "Due", render: (r) => formatDateTime(r.due_at) },
      ];

  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {rows.length === 0 ? (
        <EmptyState title={history ? "Nothing decided yet" : "You are all caught up"} text={history ? "Requests you approve or reject appear here." : "Requests waiting for your decision appear here."} />
      ) : (
        <>
          {!history && selected.length > 0 ? (
            <div className="d-flex align-items-center gap-2 mb-2">
              <span style={{ fontSize: 13 }}>{selected.length} selected</span>
              <PrimaryButton onClick={approveSelected} disabled={busy}>
                Approve selected
              </PrimaryButton>
            </div>
          ) : null}
          <RequestTable rows={rows} selectable={!history} selected={selected} onToggle={toggle} onOpen={onOpen} columns={columns} />
        </>
      )}
    </LoadState>
  );
}

function MyRequests({ onOpen, refreshKey }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getMyRequests(), [refreshKey]);
  const rows = data || [];
  const columns = [
    { label: "Request", render: (r) => r.summary || requestLabel(r.module) },
    { label: "Type", render: (r) => requestLabel(r.module) },
    { label: "Status", render: (r) => <span style={statusBadge(r.status)}>{statusLabel(r.status)}</span> },
    { label: "Step", render: (r) => (r.status === "PENDING" ? `${r.current_level} of ${r.total_levels}` : "—") },
    { label: "Submitted", render: (r) => formatDateTime(r.submitted_at) },
  ];
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {rows.length === 0 ? <EmptyState title="No requests yet" text="Leave, letter and profile requests you make appear here." /> : <RequestTable rows={rows} onOpen={onOpen} columns={columns} />}
    </LoadState>
  );
}

function DelegationForm({ show, onHide, onSaved }) {
  const [delegate, setDelegate] = useState(null);
  const [from, setFrom] = useState(todayYmd());
  const [to, setTo] = useState(todayYmd());
  const [onlyOnLeave, setOnlyOnLeave] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!delegate) return toast.error("Choose who takes over your approvals");
    setSaving(true);
    try {
      const res = await hrmsService.createDelegation({ delegate_id: delegate, from_date: from, to_date: to, only_when_on_leave: onlyOnLeave });
      if (res?.success) {
        toast.success(res.message || "Saved");
        setDelegate(null);
        onSaved();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Hand over my approvals"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <Field label="Who takes over" required>
        <EmployeeSelect value={delegate} onChange={setDelegate} />
      </Field>
      <div className="row">
        <div className="col-6">
          <Field label="From" required>
            <input type="date" className="form-control" style={inputStyle} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
        </div>
        <div className="col-6">
          <Field label="To" required>
            <input type="date" className="form-control" style={inputStyle} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </div>
      <div className="form-check">
        <input id="only-on-leave" type="checkbox" className="form-check-input" checked={onlyOnLeave} onChange={(e) => setOnlyOnLeave(e.target.checked)} />
        <label htmlFor="only-on-leave" className="form-check-label" style={{ fontSize: 14 }}>
          Only while I am on approved leave
        </label>
      </div>
    </SlidePanel>
  );
}

function Delegations() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listDelegations(), []);
  const [adding, setAdding] = useState(false);
  const rows = data || [];

  const cancel = async (id) => {
    const res = await hrmsService.cancelDelegation(id).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(res.message || "Cancelled");
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };

  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setAdding(true)}>Hand over my approvals</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No hand-overs" text="Set one up before you go on leave so requests are not held up." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["From", "To", "Dates", "When", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td style={tdStyle}>{d.delegator_name}</td>
                    <td style={tdStyle}>{d.delegate_name}</td>
                    <td style={tdStyle}>
                      {formatDate(d.from_date)} – {formatDate(d.to_date)}
                    </td>
                    <td style={tdStyle}>{d.only_when_on_leave ? "Only while on leave" : "Always"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={{ ...iconBtnStyle, fontSize: 13, fontWeight: 600 }} onClick={() => cancel(d.id)}>
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <DelegationForm
        show={adding}
        onHide={() => setAdding(false)}
        onSaved={() => {
          setAdding(false);
          reload();
        }}
      />
    </>
  );
}

export default function Approvals() {
  const [tab, setTab] = useState(TAB.TODO);
  const [openId, setOpenId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pending, setPending] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    hrmsService
      .getApprovalCount()
      .then((res) => alive && setPending(Number(res?.data?.pending) || 0))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  return (
    <div style={pageStyle}>
      <PageHeader title="Approvals" subtitle="Requests waiting for you, and the ones you have made" />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: TAB.TODO, label: "To approve", badge: pending || null },
          { key: TAB.DONE, label: "Decided" },
          { key: TAB.MINE, label: "My requests" },
          { key: TAB.DELEGATE, label: "Hand-overs" },
        ]}
      />
      {tab === TAB.TODO ? <Inbox history={false} onOpen={setOpenId} refreshKey={refreshKey} onChanged={refresh} /> : null}
      {tab === TAB.DONE ? <Inbox history onOpen={setOpenId} refreshKey={refreshKey} onChanged={refresh} /> : null}
      {tab === TAB.MINE ? <MyRequests onOpen={setOpenId} refreshKey={refreshKey} /> : null}
      {tab === TAB.DELEGATE ? <Delegations /> : null}
      <ApprovalDrawer requestId={openId} onClose={() => setOpenId(null)} onChanged={refresh} />
    </div>
  );
}
